"use client"

import { useState, useEffect, useContext } from "react"
import { Box, Grid, Card, CardContent, Typography, Button, Paper, alpha } from "@mui/material"
import { ThemeProvider, createTheme, responsiveFontSizes } from "@mui/material/styles"
import { useNavigate } from "react-router-dom"
import { ErpContext } from "../../context/ErpContext"

// Icons
import DashboardIcon from "@mui/icons-material/Dashboard"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import InventoryIcon from "@mui/icons-material/Inventory"
import PeopleIcon from "@mui/icons-material/People"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import AssessmentIcon from "@mui/icons-material/Assessment"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import PaymentIcon from "@mui/icons-material/Payment"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import PaymentsIcon from "@mui/icons-material/Payments"

// Función para verificar accesos (igual que en NavBar)
const tieneAcceso = (id: string, accesos: any[]) => {
  return accesos?.some((a) => a.idMenuAcceso === id && a.eliminado === false)
}

// Tema personalizado (solo ajustes de diseño y tipografías responsivas)
const baseTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e",
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FF8042",
    },
    info: {
      main: "#0088FE",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
})
const theme = responsiveFontSizes(baseTheme)

const HomeComponent = () => {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [userName] = useState("Mauricio") // Esto vendrá del contexto después
  const navigate = useNavigate()
  const { user } = useContext(ErpContext)

  const userRole = user?.rolUsuario
  const userAccesses = user?.accesos || []

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("es-AR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  // Secciones principales del ERP
  const erpSections = [
    {
      id: "VENTAS2",
      title: "Ver Ventas",
      description: "Gestión de transacciones y ventas",
      icon: <ShoppingCartIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
      path: "/tablaVentas",
      allowedRoles: ["Administrador", "Vendedor"],
    },
    {
      id: "COMPRAS2",
      title: "Ver Compras",
      description: "Proveedores y adquisiciones",
      icon: <LocalShippingIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
      path: "/tablaCompra",
      allowedRoles: ["Administrador"],
    },
    {
      id: "INVENTARIO3",
      title: "Ver stock",
      description: "Control de stock y productos",
      icon: <InventoryIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.warning.main,
      path: "/inventario",
      allowedRoles: ["Administrador", "Vendedor"],
    },
    {
      id: "VENTAS7",
      title: "Clientes",
      description: "Gestión de entidades y relaciones",
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.secondary.main,
      path: "/gestionCliente",
      allowedRoles: ["Administrador", "Vendedor"],
    },
    {
      id: "TRANSACCIONES6",
      title: "Cuenta Corriente",
      description: "Seguimiento de deudas y pagos",
      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.error.main,
      path: "/tablaCuentaCorriente",
      allowedRoles: ["Administrador"],
    },
    {
      id: "TRANSACCIONES8",
      title: "Ver Caja",
      description: "Control de flujo de efectivo",
      icon: <PaymentIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
      path: "/cajaComponent",
      allowedRoles: ["Administrador"],
    },
    {
      id: "VENTAS8",
      title: "Ver lista de precios",
      description: "Análisis y estadísticas",
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.main,
      path: "/tablaPrecioLista",
      allowedRoles: ["Administrador"],
    },
    {
      id: "COMPRAS5",
      title: "Ver lista de costos",
      description: "Análisis y estadísticas",
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
      path: "/tablaProveedor",
      allowedRoles: ["Administrador"],
    },
  ]

  // Accesos rápidos
  const quickActions = [
    {
      id: "VENTAS1",
      title: "Crear venta",
      icon: <ShoppingCartIcon />,
      color: theme.palette.success.main,
      path: "/gestionGenerarVenta",
      allowedRoles: ["Administrador", "Vendedor"],
    },
    {
      id: "COMPRAS1",
      title: "Crear compra",
      icon: <ShoppingCartIcon />,
      color: theme.palette.secondary.main,
      path: "/crearCompra",
      allowedRoles: ["Administrador"],
    },
    {
      id: "TRANSACCIONES4",
      title: "Crear gastos",
      icon: <PaymentsIcon />,
      color: theme.palette.info.main,
      path: "/gestionGastosComponent",
      allowedRoles: ["Administrador"],
    },
    {
      id: "INVENTARIO1",
      title: "Crear Item",
      icon: <AssessmentIcon />,
      color: theme.palette.primary.main,
      path: "/crearItem",
      allowedRoles: ["Administrador"],
    },
  ]

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          backgroundColor: "#f8f9fa",
          minHeight: "100dvh",
        }}
      >
        {/* Header de Bienvenida */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 3, md: 4 },
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
            color: "white",
          }}
        >
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, mb: { xs: 1, md: 2 } }}>
                <Box sx={{ textAlign: { xs: "left", md: "left" } }}>
                  <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}>
                    {"¡Bienvenido, "}
                    {user?.nombre || "Usuario"}
                    {"!"}
                  </Typography>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" }, opacity: 0.95 }}
                  >
                    {"Rol: "}
                    {user?.rol || "Usuario"}
                  </Typography>
                  <Typography variant="h5" sx={{ fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" }, opacity: 0.9 }}>
                    {"Lutente Tours - Tu sistema de gestión empresarial"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    mb: 1,
                  }}
                >
                  <CalendarTodayIcon sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                  <Typography
                    variant="body1"
                    sx={{ fontSize: { xs: "0.9rem", sm: "1rem" }, textTransform: "capitalize" }}
                  >
                    {formatDate(currentTime)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                  }}
                >
                  <AccessTimeIcon sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                  <Typography variant="h6" sx={{ fontFamily: "monospace", fontSize: { xs: "1rem", sm: "1.1rem" } }}>
                    {formatTime(currentTime)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Accesos Rápidos */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 3, md: 4 }, borderRadius: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
          >
            <TrendingUpIcon sx={{ mr: 2, color: theme.palette.secondary.main, fontSize: { xs: 20, sm: 24 } }} />
            {"Accesos Rápidos"}
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {quickActions.map((action, i) => {
              const hasAccess = tieneAcceso(action.id, userAccesses)
              return (
                <Grid item xs={6} md={3} key={i}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => hasAccess && navigate(action.path)}
                    disabled={!hasAccess}
                    startIcon={action.icon}
                    sx={{
                      py: { xs: 1, sm: 1.5, md: 2 },
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                      borderColor: hasAccess ? action.color : alpha(theme.palette.text.primary, 0.4),
                      color: hasAccess ? action.color : alpha(theme.palette.text.primary, 0.4),
                      borderWidth: 2,
                      pointerEvents: hasAccess ? "auto" : "none",
                      "& .MuiButton-startIcon": {
                        mr: { xs: 0.5, sm: 1 },
                        "& > *:nth-of-type(1)": { fontSize: { xs: 18, sm: 20 } },
                      },
                    }}
                  >
                    {action.title}
                  </Button>
                </Grid>
              )
            })}
          </Grid>
        </Paper>

        {/* Secciones Principales del ERP */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
          >
            <DashboardIcon sx={{ mr: 2, color: theme.palette.secondary.main, fontSize: { xs: 20, sm: 24 } }} />
            {"Módulos del Sistema"}
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {erpSections.map((section, idx) => {
              const hasAccess = tieneAcceso(section.id, userAccesses)
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                  <Card
                    onClick={() => hasAccess && navigate(section.path)}
                    sx={{
                      height: "100%",
                      cursor: hasAccess ? "pointer" : "default",
                      opacity: hasAccess ? 1 : 0.5,
                      pointerEvents: hasAccess ? "auto" : "none",
                      border: `2px solid transparent`,
                      "&:hover": hasAccess ? { borderColor: section.color } : {},
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2, sm: 3 },
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 64, sm: 72, md: 80 },
                          height: { xs: 64, sm: 72, md: 80 },
                          borderRadius: "50%",
                          backgroundColor: alpha(section.color, 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                          color: section.color,
                          "& svg": {
                            fontSize: { xs: 28, sm: 34, md: 40 },
                          },
                        }}
                      >
                        {section.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ color: section.color, fontWeight: 600, fontSize: { xs: "1rem", md: "1.1rem" } }}
                      >
                        {section.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          flexGrow: 1,
                          mt: 0.5,
                          display: "-webkit-box",
                          WebkitLineClamp: { xs: 2, md: 3 },
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {section.description}
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={!hasAccess}
                        sx={{
                          mt: 2,
                          backgroundColor: section.color,
                          py: { xs: 1, sm: 1.25 },
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                          pointerEvents: hasAccess ? "auto" : "none",
                          "&:hover": { backgroundColor: hasAccess ? alpha(section.color, 0.8) : undefined },
                        }}
                      >
                        {"Acceder"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Paper>

        {/* Footer con información adicional */}
        <Box sx={{ mt: { xs: 3, md: 4 }, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
            {"Sistema ERP - Versión 1.0 | Desarrollado para gestión empresarial integral"}
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export default HomeComponent
